import { Nullable } from '@common/typescript/objects/Nullable';
import { WithId } from '@common/typescript/objects/WithId';
import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import { ClearValue } from '@common/react/utils/utils';

export interface Dictionary<T> {
	[key: string]: T;
}

export interface NumberKeyedDictionary<T> {
	[key: number]: T;
}

export enum Mode {
	View,
	Edit
}

export type StringBoolNumber = string | boolean | number;

/**
 * filter value change object
 * @interface HandleChangeEventElement
 */
export interface HandleChangeEventElement {
	currentTarget: {
		name: string;
		value: Nullable<StringBoolNumber | Array<StringBoolNumber>>;
	};
}

export type EqualPredicateDelegate<T> = (a: T, b: T) => boolean;

export const defaultEqualPredicate = <T extends WithId>(a: T, b: T) => a.id === b.id;

/**
 * TransformAfterSaveParams - options for TransformAfterSaveDelegate
 *
 * @interface ClearForSubmitParams
 * @template {WithDeleted} T - item type
 * @template TResponse - response type
 */
export interface TransformAfterSaveParams<T extends WithDeleted, TResponse = T> {
	/**
	 * item that was sent to server (before clearForSubmit applied) with id overridden with value from server
	 */
	item: T;
	/**
	 * server response
	 */
	response: TResponse;
	/**
	 * item with client id. value before editing the element. In rare cases, this may be useful.
	 */
	prevItem: T;
	/**
	 * function to update inner id of fields list inside item
	 *
	 * returns an object with keys from the fields based on the item, modifying the values with names from properties
	 *
	 * i.e. item[path]: response[path]
	 *
	 * @example return item with updated field 'id' inside each item['items'] taken from response['items']
	 * returnAfterSubmit(item, ['items'], response);
	 * @example return item with updated field 'fieldName' inside each item['items'] taken from response['items']
	 * returnAfterSubmit(item, ['items'], response, ['fieldName']);
	 *
	 * @example
	 let item = {
	    otherData: { array: ['t', 'e', 's', 't'] },
	    items: [{ id: -1 }, { id: -2, other: 'test1' }],
	    values: [{ id: -1, innerItems: [{ id: -1, other: 'test', someKey: -10 }, { id: -1, other: 'test2' }] }]
	 }
	 let response = {
	    responseOtherData: { someValue: '' },
	    items: [{ id: 1023 }, { id: 223 }],
	    values: [{ id: 45646, innerItems: [{ id: 345, someKey: 52 }, { id: 535, other2: '' }] }]
	 }

	 console.log(returnAfterSubmit(item, ['items'], response, ['id']));
	 // {
	 //      items: [{ id: 1023 }, { id: 223, other: 'test1' }],
	 // }

	 console.log(returnAfterSubmit(item, ['items', 'values'], response, ['id']));
	 // {
	 //      items: [{ id: 1023 }, { id: 223, other: 'test1' }],
	 //      values: [{ id: 45646, innerItems: [{ id: -1, other: 'test', someKey: -10 }, { id: -1, other: 'test2' }] }]
	 // }

	 console.log(returnAfterSubmit(item, ['items', { values: ['innerItems'] }], response, ['id']));
	 // {
	 //      items: [{ id: 1023 }, { id: 223, other: 'test1' }],
	 //      values: [{ id: 45646, innerItems: [{ id: 345, other: 'test', someKey: -10 }, { id: 535, other: 'test2' }] }]
	 // }

	 console.log(returnAfterSubmit(item, ['items', { values: ['innerItems'] }], response, ['id', 'someKey']));
	 // {
	 //      items: [{ id: 1023 }, { id: 223, other: 'test1' }],
	 //      values: [{ id: 45646, innerItems: [{ id: 345, other: 'test', someKey: 52 }, { id: 535, other: 'test2' }] }]
	 // }
	 * @param values - item where list fields will be updated
	 * @param fields - fields where need to update properties with value from response object
	 * @param response - response object (must have the same structure as values)
	 * @param properties - array of properties that fill copied from response
	 * @returns updated item
	 */
	returnAfterSubmit: (values: T | undefined, fields: Array<string | object>, response?: TResponse, properties?: Array<string>) =>
		T | object | undefined;
}

/**
 * used to update properties from server response
 *
 * @template {WithDeleted} T - item type
 * @template TResponse - response type
 * @param params - contains item, prevItem, response and returnAfterSubmit func {@link TransformAfterSaveParams}
 * @returns updated item
 */
export type TransformAfterSaveDelegate<T extends WithDeleted, TResponse = T> = (
	/**
	 * contains item, prevItem, response and returnAfterSubmit func
	 */
	params: TransformAfterSaveParams<T, TResponse>,
) => T;

export type ClearListFieldsDelegate = <TItems, >(items: Array<TItems>, fields: Array<string>) => Array<TItems>;

/**
 * ClearForSubmitParams - options for ClearForSubmitDelegate
 *
 * @interface ClearForSubmitParams
 * @template {WithDeleted} T - item type
 * @param item - item for clearing
 * @param clearListFields - function to clear fields at list items
 */
export interface ClearForSubmitParams<T extends WithDeleted> {
	/**
	 * item for clearing
	 */
	item: T;
	/**
	 * function to clear fields at list items
	 * @param items - array of items
	 * @param fields - array of fields for clear
	 * @returns cleared array
	 */
	clearListFields: ClearListFieldsDelegate;
}

/**
 * used to remove redundant or unwanted properties from item
 *
 * @template {WithDeleted} T - item type
 * @param params - contains item and clearListFields func {@link ClearForSubmitParams}
 * @returns object with overridden fields
 */
export type ClearForSubmitDelegate<T extends WithDeleted> = (
	/**
	 * contains item and clearListFields func
	 */
	params: ClearForSubmitParams<T>,
) => ClearValue<T> | T;
